import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACTION_TYPES, useAmendmentDetailDispatch, useAmendmentDetailState } from './amendment-detail-context';
import { DrawerName } from './amendment-detail-reducer';
import AmendmentAppService from '../../../../../application/platform/sge/amendment/amendment-app-service';
import CommitmentAppService from '../../../../../application/platform/sge/commitment/commitment-app-service';
import LiquidationAppService from '../../../../../application/platform/sge/liquidation/liquidation-app-service';
import PaymentAppService from '../../../../../application/platform/sge/payment/payment-app-service';
import TransferAppService from '../../../../../application/platform/sge/transfer/transfer-app-service';
import AccountReportAppService from '../../../../../application/platform/sge/account-report/account-report-app-service';
import AttachmentAppService from '../../../../../application/platform/sge/attachment/attachment-app-service';
import ParlamentarianAppService from '../../../../../application/platform/sge/parlamentarian/parlamentarian-app-service';
import PoliticalPartyAppService from '../../../../../application/platform/sge/political-party/political-party-app-service';
import SupplierAppService from '../../../../../application/platform/sge/supplier/supplier-app-service';
import { useFeedback } from '../../../../../hooks/use-feedback';
import { ICommitmentResponse } from '../../../../../model/sge/commitment/response/commitment-response';
import { ILiquidationResponse } from '../../../../../model/sge/liquidation/response/liquidation-response';
import { IPaymentResponse } from '../../../../../model/sge/payment/response/payment-response';
import { ITransferResponse } from '../../../../../model/sge/transfer/response/transfer-response';
import { IAccountReportResponse } from '../../../../../model/sge/account-report/response/account-report-response';
import { ICommitmentAddEditRequest } from '../../../../../model/sge/commitment/request/commitment-add-edit-request';
import { ILiquidationAddEditRequest } from '../../../../../model/sge/liquidation/request/liquidation-add-edit-request';
import { IPaymentAddEditRequest } from '../../../../../model/sge/payment/request/payment-add-edit-request';
import { ITransferAddEditRequest } from '../../../../../model/sge/transfer/request/transfer-add-edit-request';
import { IAccountReportAddEditRequest } from '../../../../../model/sge/account-report/request/account-report-add-edit-request';

const LIST_PARAMS = { page: 0, pageSize: 200 };
const AMENDMENT_ENTITY = 'Amendment';
const ACCOUNT_REPORT_ENTITY = 'AmendmentAccountReport';

function saveBlob(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
}

export const useAmendmentDetailController = () => {
    const navigate = useNavigate();
    const dispatch = useAmendmentDetailDispatch();
    const state = useAmendmentDetailState();
    const { showSuccess, showError, confirmDelete, withLoading } = useFeedback();
    const [isSaving, setIsSaving] = useState(false);
    const amendmentId = Number(state.amendmentId);

    useEffect(() => {
        withLoading(async () => {
            const [amendment, parlamentarians, politicalParties, suppliers] = await Promise.all([
                AmendmentAppService.getById(amendmentId),
                ParlamentarianAppService.list({ page: 0, pageSize: 100 }),
                PoliticalPartyAppService.listAll(),
                SupplierAppService.list({ page: 0, pageSize: 100 }),
            ]);
            dispatch({ type: ACTION_TYPES.setAmendment, payload: amendment });
            dispatch({ type: ACTION_TYPES.setLookups, payload: { parlamentarians: parlamentarians.items, politicalParties, suppliers: suppliers.items } });
        }).catch((error: any) => showError('Não foi possível carregar a emenda', error));
        loadCommitments();
        loadTransfers();
        loadAccountReports();
        loadAttachments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Carregamento das listas
    function loadList<T>(load: () => Promise<T>, actionType: ACTION_TYPES, errorMessage: string) {
        load()
            .then((payload) => dispatch({ type: actionType, payload }))
            .catch((error: any) => showError(errorMessage, error));
    }

    function loadCommitments() {
        loadList(() => CommitmentAppService.list(amendmentId, LIST_PARAMS).then((r) => r.items), ACTION_TYPES.setCommitments, 'Não foi possível carregar os empenhos');
    }

    function loadLiquidations(commitmentId: number) {
        loadList(() => LiquidationAppService.list(commitmentId, LIST_PARAMS).then((r) => r.items), ACTION_TYPES.setLiquidations, 'Não foi possível carregar as liquidações');
    }

    function loadPayments(liquidationId: number) {
        loadList(() => PaymentAppService.list(liquidationId, LIST_PARAMS).then((r) => r.items), ACTION_TYPES.setPayments, 'Não foi possível carregar os pagamentos');
    }

    function loadTransfers() {
        loadList(() => TransferAppService.list(amendmentId, LIST_PARAMS).then((r) => r.items), ACTION_TYPES.setTransfers, 'Não foi possível carregar as transferências');
    }

    function loadAccountReports() {
        loadList(() => AccountReportAppService.list(amendmentId, LIST_PARAMS).then((r) => r.items), ACTION_TYPES.setAccountReports, 'Não foi possível carregar as prestações de contas');
    }

    function loadAttachments() {
        loadList(() => AttachmentAppService.list(AMENDMENT_ENTITY, amendmentId), ACTION_TYPES.setAttachments, 'Não foi possível carregar os anexos');
    }

    function loadAccountReportAttachments(accountReportId: number) {
        loadList(() => AttachmentAppService.list(ACCOUNT_REPORT_ENTITY, accountReportId), ACTION_TYPES.setAccountReportAttachments, 'Não foi possível carregar os anexos da prestação de contas');
    }

    // Navegação: a edição volta para o detalhe ao cancelar
    function navigateToEdit() { navigate(`/emendas/editar/${amendmentId}`, { state: { from: `/emendas/${amendmentId}` } }); }
    function navigateToList() { navigate('/emendas'); }

    function handleTabChange(_: React.SyntheticEvent, tabIndex: number) {
        dispatch({ type: ACTION_TYPES.setTabIndex, payload: tabIndex });
    }

    function handleToggleCommitment(commitment: ICommitmentResponse) {
        const isSelected = state.selectedCommitment?.id === commitment.id;
        dispatch({ type: ACTION_TYPES.setSelectedCommitment, payload: isSelected ? undefined : commitment });
        if (!isSelected) loadLiquidations(commitment.id);
    }

    function handleToggleLiquidation(liquidation: ILiquidationResponse) {
        const isExpanded = state.expandedLiquidationId === liquidation.id;
        dispatch({ type: ACTION_TYPES.setExpandedLiquidationId, payload: isExpanded ? undefined : liquidation.id });
        if (!isExpanded) loadPayments(liquidation.id);
    }

    // Drawers
    function openDrawer(name: DrawerName, extra: Record<string, unknown> = {}) {
        dispatch({ type: ACTION_TYPES.openDrawer, payload: { name, ...extra } });
    }

    function closeDrawer(name: DrawerName) {
        dispatch({ type: ACTION_TYPES.closeDrawer, payload: name });
    }

    // Salva, fecha o drawer e recarrega a lista; erros mantêm o drawer aberto
    async function saveFromDrawer(drawer: DrawerName, save: () => Promise<void>, successMessage: string, errorMessage: string, reload: () => void) {
        setIsSaving(true);
        try {
            await save();
            showSuccess(successMessage);
            closeDrawer(drawer);
            reload();
        } catch (error: any) {
            showError(errorMessage, error);
        } finally {
            setIsSaving(false);
        }
    }

    function deleteWithConfirmation(message: string, remove: () => Promise<void>, successMessage: string, errorMessage: string, reload: () => void) {
        confirmDelete(message, async () => {
            try {
                await remove();
                showSuccess(successMessage);
                reload();
            } catch (error: any) {
                showError(errorMessage, error);
            }
        });
    }

    // Empenhos
    function saveCommitment(request: ICommitmentAddEditRequest) {
        const editing = state.commitmentDrawer.record;
        return saveFromDrawer(
            'commitmentDrawer',
            () => (editing ? CommitmentAppService.edit(amendmentId, editing.id, request) : CommitmentAppService.add(amendmentId, request)),
            editing ? 'Empenho atualizado com sucesso!' : 'Empenho adicionado com sucesso!',
            'Não foi possível salvar o empenho',
            loadCommitments,
        );
    }

    function deleteCommitment(commitment: ICommitmentResponse) {
        deleteWithConfirmation(
            `Tem certeza que deseja excluir o empenho "${commitment.commitmentNumber || ''}"?`,
            () => CommitmentAppService.remove(amendmentId, commitment.id),
            'Empenho excluído com sucesso!',
            'Não foi possível excluir o empenho',
            loadCommitments,
        );
    }

    // Liquidações (sempre do empenho selecionado)
    function saveLiquidation(request: ILiquidationAddEditRequest) {
        const commitment = state.selectedCommitment;
        if (!commitment) return;
        const editing = state.liquidationDrawer.record;
        return saveFromDrawer(
            'liquidationDrawer',
            () => (editing ? LiquidationAppService.edit(commitment.id, editing.id, request) : LiquidationAppService.add(commitment.id, request)),
            editing ? 'Liquidação atualizada com sucesso!' : 'Liquidação adicionada com sucesso!',
            'Não foi possível salvar a liquidação',
            () => { loadLiquidations(commitment.id); loadCommitments(); },
        );
    }

    function deleteLiquidation(liquidation: ILiquidationResponse) {
        const commitment = state.selectedCommitment;
        if (!commitment) return;
        deleteWithConfirmation(
            `Tem certeza que deseja excluir a liquidação "${liquidation.liquidationNumber || ''}"?`,
            () => LiquidationAppService.remove(commitment.id, liquidation.id),
            'Liquidação excluída com sucesso!',
            'Não foi possível excluir a liquidação',
            () => { loadLiquidations(commitment.id); loadCommitments(); },
        );
    }

    // Pagamentos (sempre da liquidação expandida)
    function openPaymentDrawer(liquidation: ILiquidationResponse, payment?: IPaymentResponse) {
        openDrawer('paymentDrawer', { liquidation, record: payment });
    }

    function savePayment(request: IPaymentAddEditRequest) {
        const { liquidation, record: editing } = state.paymentDrawer;
        if (!liquidation) return;
        return saveFromDrawer(
            'paymentDrawer',
            () => (editing ? PaymentAppService.edit(liquidation.id, editing.id, request) : PaymentAppService.add(liquidation.id, request)),
            editing ? 'Pagamento atualizado com sucesso!' : 'Pagamento adicionado com sucesso!',
            'Não foi possível salvar o pagamento',
            () => { loadPayments(liquidation.id); loadCommitments(); },
        );
    }

    function deletePayment(liquidation: ILiquidationResponse, payment: IPaymentResponse) {
        deleteWithConfirmation(
            `Tem certeza que deseja excluir o pagamento "${payment.paymentOrderNumber || ''}"?`,
            () => PaymentAppService.remove(liquidation.id, payment.id),
            'Pagamento excluído com sucesso!',
            'Não foi possível excluir o pagamento',
            () => { loadPayments(liquidation.id); loadCommitments(); },
        );
    }

    // Transferências
    function saveTransfer(request: ITransferAddEditRequest) {
        const editing = state.transferDrawer.record;
        return saveFromDrawer(
            'transferDrawer',
            () => (editing ? TransferAppService.edit(amendmentId, editing.id, request) : TransferAppService.add(amendmentId, request)),
            editing ? 'Transferência atualizada com sucesso!' : 'Transferência adicionada com sucesso!',
            'Não foi possível salvar a transferência',
            loadTransfers,
        );
    }

    function deleteTransfer(transfer: ITransferResponse) {
        deleteWithConfirmation(
            `Tem certeza que deseja excluir a transferência para "${transfer.companyName || ''}"?`,
            () => TransferAppService.remove(amendmentId, transfer.id),
            'Transferência excluída com sucesso!',
            'Não foi possível excluir a transferência',
            loadTransfers,
        );
    }

    // Prestações de contas
    function openAccountReportDrawer(report?: IAccountReportResponse) {
        openDrawer('accountReportDrawer', { record: report });
        if (report) loadAccountReportAttachments(report.id);
    }

    function saveAccountReport(request: IAccountReportAddEditRequest) {
        const editing = state.accountReportDrawer.record;
        return saveFromDrawer(
            'accountReportDrawer',
            () => (editing ? AccountReportAppService.edit(amendmentId, editing.id, request) : AccountReportAppService.add(amendmentId, request)),
            editing ? 'Prestação de contas atualizada com sucesso!' : 'Prestação de contas adicionada com sucesso!',
            'Não foi possível salvar a prestação de contas',
            loadAccountReports,
        );
    }

    function deleteAccountReport(report: IAccountReportResponse) {
        deleteWithConfirmation(
            'Tem certeza que deseja excluir esta prestação de contas?',
            () => AccountReportAppService.remove(amendmentId, report.id),
            'Prestação de contas excluída com sucesso!',
            'Não foi possível excluir a prestação de contas',
            loadAccountReports,
        );
    }

    function deleteAccountReportAttachment(attachmentId: number) {
        const reportId = state.accountReportDrawer.record?.id;
        deleteWithConfirmation(
            'Tem certeza que deseja excluir este anexo?',
            () => AttachmentAppService.remove(attachmentId),
            'Anexo excluído com sucesso!',
            'Não foi possível excluir o anexo',
            () => { if (reportId) loadAccountReportAttachments(reportId); },
        );
    }

    // Anexos da emenda
    async function uploadAttachment(file: File) {
        try {
            await withLoading(() => AttachmentAppService.upload({ entityType: AMENDMENT_ENTITY, entityId: amendmentId, file }));
            showSuccess('Anexo enviado com sucesso!');
            loadAttachments();
        } catch (error: any) {
            showError('Não foi possível enviar o anexo', error);
        }
    }

    async function downloadAttachment(attachmentId: number, fileName: string) {
        try {
            saveBlob(await AttachmentAppService.download(attachmentId), fileName);
        } catch (error: any) {
            showError('Não foi possível baixar o anexo', error);
        }
    }

    function deleteAttachment(attachmentId: number) {
        deleteWithConfirmation(
            'Tem certeza que deseja excluir este anexo?',
            () => AttachmentAppService.remove(attachmentId),
            'Anexo excluído com sucesso!',
            'Não foi possível excluir o anexo',
            loadAttachments,
        );
    }

    return {
        isSaving, navigateToEdit, navigateToList, handleTabChange, handleToggleCommitment, handleToggleLiquidation,
        openDrawer, closeDrawer,
        saveCommitment, deleteCommitment,
        saveLiquidation, deleteLiquidation,
        openPaymentDrawer, savePayment, deletePayment,
        saveTransfer, deleteTransfer,
        openAccountReportDrawer, saveAccountReport, deleteAccountReport, deleteAccountReportAttachment,
        uploadAttachment, downloadAttachment, deleteAttachment,
    };
};

export type AmendmentDetailController = ReturnType<typeof useAmendmentDetailController>;
